import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { BlogCategoryDto } from '../../../shared/models/blog.models';
import { BlogsService } from '../../../shared/services/blogs.service';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminToastService,
} from '../../../shared/admin';
import { PermissionService } from '../../permissions/permission.service';

@Component({
  selector: 'app-platform-blog-categories-page',
  standalone: true,
  imports: [FormsModule, AdminPageHeaderComponent, AdminCardComponent, AdminButtonComponent, AdminModalComponent, AdminEmptyStateComponent],
  template: `
    <section>
      <app-admin-page-header title="Blog categories">
        <ng-container actions>
          @if (showCreate()) {
            <app-admin-button variant="primary" type="button" (click)="openCreate()" [disabled]="!canCreate()">New category</app-admin-button>
          }
        </ng-container>
      </app-admin-page-header>
      <div class="cg-admin-grid cg-admin-grid--2">
        @for (item of items(); track item._id) {
          <app-admin-card>
            <div class="font-medium">{{ item.name }}</div>
            <p class="cg-form-help">/blogs/category/{{ item.slug }}</p>
            <div class="mt-3 flex gap-2">
              @if (showEdit()) {
                <app-admin-button variant="ghost" type="button" (click)="edit(item)" [disabled]="!canEdit()">Edit</app-admin-button>
              }
              @if (showDelete()) {
                <app-admin-button variant="danger" type="button" (click)="remove(item)" [disabled]="!canDelete()">Delete</app-admin-button>
              }
            </div>
          </app-admin-card>
        }
        @if (items().length === 0) {
          <app-admin-empty-state icon="folder" title="No categories yet" />
        }
      </div>
      @if (showForm()) {
        <app-admin-modal [title]="editingId() ? 'Edit category' : 'New category'" (closed)="closeForm()">
          <label class="block text-sm">
            <span>Name</span>
            <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.name" name="name" />
          </label>
          <label class="block text-sm mt-3">
            <span>Description</span>
            <textarea class="mt-1 w-full rounded border p-2" rows="2" [(ngModel)]="form.description" name="description"></textarea>
          </label>
          <div class="mt-4 flex gap-2">
            <app-admin-button variant="primary" type="button" (click)="save()">Save</app-admin-button>
          </div>
        </app-admin-modal>
      }
    </section>
  `,
})
export class PlatformBlogCategoriesPageComponent implements OnInit {
  private readonly blogs = inject(BlogsService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly toast = inject(AdminToastService);
  private readonly permissions = inject(PermissionService);

  readonly showCreate = computed(() => this.permissions.canShow('website.blogs', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.blogs', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('website.blogs', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.blogs', 'edit'));
  readonly showDelete = computed(() => this.permissions.canShow('website.blogs', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('website.blogs', 'delete'));

  readonly items = signal<BlogCategoryDto[]>([]);
  readonly showForm = signal(false);
  readonly editingId = signal<string | null>(null);
  form = { name: '', description: '' };

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.blogs.listCategories().subscribe((items) => this.items.set(items));
  }

  openCreate(): void {
    this.editingId.set(null);
    this.form = { name: '', description: '' };
    this.showForm.set(true);
  }

  edit(item: BlogCategoryDto): void {
    this.editingId.set(item._id);
    this.form = { name: item.name, description: item.description ?? '' };
    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
  }

  save(): void {
    const id = this.editingId();
    const req = id
      ? this.blogs.updateCategory(id, this.form)
      : this.blogs.createCategory(this.form);
    req.subscribe({
      next: () => {
        this.toast.success(id ? 'Category updated.' : 'Category created.');
        this.closeForm();
        this.reload();
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not save category.'),
    });
  }

  async remove(item: BlogCategoryDto): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete category',
      message: `Delete "${item.name}"? Posts in this category will need to be reassigned.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    this.blogs.removeCategory(item._id).subscribe({
      next: () => {
        this.toast.success(`"${item.name}" deleted.`);
        this.reload();
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not delete category.'),
    });
  }
}
